import { useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { outletGrowth } from "@/data/markets";
import { useLang } from "@/i18n";

const SERIES = [
  { key: "HR", color: "#dd260e" },
  { key: "BA", color: "#000000" },
  { key: "PL", color: "#0072ce" },
  { key: "SI", color: "#9b6bff" },
  { key: "MK", color: "#22c55e" },
  { key: "RS", color: "#f59e0b" },
  { key: "RO", color: "#ec4899" },
];

const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useLang();
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((acc, p) => acc + (p.value || 0), 0);
  // Sort payload by value desc for readability
  const sorted = [...payload].sort((a, b) => b.value - a.value);
  return (
    <div className="bg-[#000000] border border-[#000000] rounded-sm shadow-xl p-3 min-w-[180px]">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#dd260e] mb-2">
        {label}
      </p>
      <p className="text-xs text-white/60 mb-1">{t("Total outlets")}</p>
      <p className="font-condensed text-2xl font-black text-white mb-3">
        {total.toLocaleString()}
      </p>
      <div className="space-y-1 pt-2 border-t border-white/10">
        {sorted.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-2 text-white/80">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
              {p.dataKey}
            </span>
            <span className="font-bold text-white tabular-nums">
              {p.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const OutletGrowthChart = () => {
  const { t } = useLang();
  const [hidden, setHidden] = useState({});

  const toggleSeries = (key) => {
    setHidden((h) => ({ ...h, [key]: !h[key] }));
  };

  const lastMonth = outletGrowth[outletGrowth.length - 1];
  const firstMonth = outletGrowth[0];
  const totalLast = SERIES.reduce((acc, s) => acc + (lastMonth[s.key] || 0), 0);
  const totalFirst = SERIES.reduce((acc, s) => acc + (firstMonth[s.key] || 0), 0);
  const growth = totalLast - totalFirst;
  const growthPct = totalFirst > 0 ? Math.round((growth / totalFirst) * 100) : 0;

  return (
    <div className="bg-white border border-[#e8e8e8] rounded-sm p-5 sm:p-6" data-testid="outlet-growth-chart">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="cchbc-eyebrow mb-2">{t("12-month rollout")}</span>
          <h3 className="font-condensed text-2xl font-black uppercase tracking-tight text-[#000000] mt-3">
            {t("Outlet Growth Across Markets")}
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-[#555555] font-bold uppercase tracking-[0.18em]">
            {t("Net new outlets")}
          </p>
          <p className="font-condensed text-3xl font-black uppercase tracking-tight text-[#000000]">
            +{growth.toLocaleString()}
          </p>
          <p className="text-[10px] text-[#dd260e] font-bold uppercase tracking-[0.15em]">
            +{growthPct}% YoY
          </p>
        </div>
      </div>

      {/* Custom legend with toggling */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="chart-legend">
        {SERIES.map((s) => {
          const off = hidden[s.key];
          return (
            <button
              key={s.key}
              onClick={() => toggleSeries(s.key)}
              className={`inline-flex items-center gap-2 px-2.5 py-1 border rounded-sm text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                off
                  ? "border-[#e8e8e8] text-[#bbb] bg-[#fafafa]"
                  : "border-[#e8e8e8] text-[#000000] bg-white hover:border-[#000000]"
              }`}
              data-testid={`legend-${s.key}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: off ? "#ddd" : s.color }}
              />
              {s.key}
            </button>
          );
        })}
      </div>

      <div className="w-full h-[320px] sm:h-[380px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={outletGrowth} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
            <defs>
              {SERIES.map((s) => (
                <linearGradient key={s.key} id={`gradient-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 4" stroke="#e8e8e8" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: "#555555", fontFamily: "Barlow, sans-serif" }}
              tickLine={false}
              axisLine={{ stroke: "#e8e8e8" }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#555555", fontFamily: "Barlow, sans-serif" }}
              tickLine={false}
              axisLine={{ stroke: "#e8e8e8" }}
              tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#000000", strokeWidth: 1, strokeDasharray: "3 4" }} />
            {SERIES.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stackId="1"
                stroke={s.color}
                strokeWidth={1.5}
                fill={`url(#gradient-${s.key})`}
                hide={!!hidden[s.key]}
                isAnimationActive
                animationDuration={600}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-[#555555] italic uppercase tracking-[0.1em] mt-3">
        {t("Cumulative active outlets per market — click any country in the legend to toggle.")}
      </p>
    </div>
  );
};
